import { buildApiUrl, fetchJSON, validateRequiredParams } from './crm/crmApi';

export type ProactiveDigestType = 'morning' | 'lunch' | 'alert';

export type ProactiveRolePreset = 'solo_founder' | 'sales_led_smb' | 'agency' | 'ecom';

export interface ProactiveDigestSettings {
  user_id: string;
  organization_id: string | null;
  enabled: boolean;
  timezone: string;
  morning_enabled: boolean;
  lunch_enabled: boolean;
  morning_time: string;
  lunch_time: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  deal_alerts_enabled: boolean;
  news_topics: string[];
  role_preset?: ProactiveRolePreset;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  deal_alert_min_amount?: number;
  news_max_per_week?: number;
  last_morning_sent_date: string | null;
  last_lunch_sent_date: string | null;
  last_deal_alert_checked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProactiveDigestRun {
  id: string;
  user_id: string;
  organization_id: string | null;
  digest_type: ProactiveDigestType;
  for_date: string;
  scheduled_for: string | null;
  generated_at: string;
  status: 'processing' | 'sent' | 'failed' | 'skipped';
  is_test?: boolean;
  metrics: any;
  summary_text: string | null;
  email_sent: boolean;
  in_app_sent: boolean;
  error: string | null;
  created_at: string;
}

export interface ProactiveOrgTargets {
  organization_id: string;
  monthly_revenue_target: number;
  weekly_meetings_target: number;
  updated_at: string;
}

export interface ProactiveUserTargets {
  user_id: string;
  organization_id: string;
  monthly_revenue_target: number | null;
  weekly_meetings_target: number | null;
  updated_at: string;
}

const proactiveDigestsService = {
  async getSettings(): Promise<{ data: ProactiveDigestSettings | null }>{
    const url = buildApiUrl('/api/proactive-digests/settings');
    return fetchJSON(url);
  },

  async updateSettings(patch: Partial<Omit<ProactiveDigestSettings, 'user_id' | 'created_at' | 'updated_at'>> & { organization_id?: string | null }): Promise<{ data: ProactiveDigestSettings }>{
    const url = buildApiUrl('/api/proactive-digests/settings');
    return fetchJSON(url, { method: 'PUT', body: patch });
  },

  async createTestRun(params?: { digest_type?: 'morning' | 'lunch' | 'news' }): Promise<{ data: { id: string } }>{
    const url = buildApiUrl('/api/proactive-digests/test-run');
    const body = params?.digest_type ? { digest_type: params.digest_type } : {};
    return fetchJSON(url, { method: 'POST', body });
  },

  async listRuns(limit = 30): Promise<{ data: ProactiveDigestRun[] }>{
    const url = buildApiUrl('/api/proactive-digests/runs', { limit });
    return fetchJSON(url);
  },

  async getRun(id: string): Promise<{ data: ProactiveDigestRun }>{
    validateRequiredParams({ id }, ['id'], { service: 'proactiveDigestsService.getRun' });
    const url = buildApiUrl(`/api/proactive-digests/runs/${encodeURIComponent(id)}`);
    return fetchJSON(url);
  },

  async getTargets(organization_id: string): Promise<{ data: { org: ProactiveOrgTargets | null; user: ProactiveUserTargets | null } }>{
    validateRequiredParams({ organization_id }, ['organization_id'], { service: 'proactiveDigestsService.getTargets' });
    const url = buildApiUrl('/api/proactive-digests/targets', { organization_id });
    return fetchJSON(url);
  },

  async updateUserTargets(params: { organization_id: string; monthly_revenue_target?: number; weekly_meetings_target?: number }): Promise<{ data: ProactiveUserTargets }>{
    validateRequiredParams(params, ['organization_id'], { service: 'proactiveDigestsService.updateUserTargets' });
    const url = buildApiUrl('/api/proactive-digests/targets/user');
    return fetchJSON(url, { method: 'PUT', body: params });
  },

  async updateOrgTargets(params: { organization_id: string; monthly_revenue_target?: number; weekly_meetings_target?: number }): Promise<{ data: ProactiveOrgTargets }>{
    validateRequiredParams(params, ['organization_id'], { service: 'proactiveDigestsService.updateOrgTargets' });
    const url = buildApiUrl('/api/proactive-digests/targets/org');
    return fetchJSON(url, { method: 'PUT', body: params });
  }
};

export default proactiveDigestsService;
